import React, { useState } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';

import {
	Button,
	ControlledInput,
	Flex,
	Label,
	mediaQueries,
	ParentShimmer,
} from '@zeal/web-ui';
import { useMediaQuery } from '@zeal/zeal-lib';

import { useForgotPassword } from '@app/data/ForgotPassword/useForgotMyPassword';

import { TForgotPasswordForm } from '../types';

interface ForgotPasswordFormProps {
	setResetEmailSent: (value: boolean) => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
	setResetEmailSent,
}) => {
	const { t } = useTranslation('forgotPassword');
	const isDesktop = useMediaQuery(mediaQueries.min.md);
	const navigate = useNavigate();

	const [showError, setShowError] = useState(false);

	const formSchema = yup.object({
		email: yup.string().email().required(t('email_error')),
	});

	const formMethods = useForm<TForgotPasswordForm>({
		defaultValues: {
			email: '',
		},
		resolver: yupResolver(formSchema),
		mode: 'onSubmit',
	});
	const {
		handleSubmit,
		formState: { isDirty, errors },
	} = formMethods;

	const { isError, isPending, doForgetPassword } = useForgotPassword({
		onSuccess: () => {
			setResetEmailSent(true);
			return null;
		},
	});

	const handleSubmitEmail = ({ email }: TForgotPasswordForm) => {
		setShowError(true);
		doForgetPassword({ email });
	};

	const handleCancel = () => navigate('/');

	return (
		<ParentShimmer>
			<FormProvider {...formMethods}>
				<Flex isColumn gap="6" align="center">
					<Flex
						gap="12"
						isColumn
						justify={isDesktop ? 'center' : 'start'}
						className="h-full w-full px-4 pt-6 pb-8 md:py-12 md:px-7"
						style={{
							width: isDesktop ? '500px' : '343px',
						}}
					>
						<Flex gap="2" isColumn>
							<Label.Big600 textAlign="start">
								{t('forgot_password_title')}
							</Label.Big600>
							<Label.Thin300 textAlign="start" variant="subdued">
								{t('forgot_password_subtitle')}
							</Label.Thin300>
						</Flex>
						<ControlledInput<TForgotPasswordForm>
							label={t('email')}
							name="email"
							type="email"
							className="w-full"
							startIconProps={{
								iconName: 'envelope',
							}}
							placeholder={t('email_placeholder')}
							isError={showError && (isError || !!errors.email)}
							message={
								showError &&
								(isError ? t('can_not_find_account') : errors.email?.message)
							}
						/>
						<Flex gap="4" isColumn align="center">
							<Button
								variant="primary"
								size="lg"
								className="w-full"
								isLoading={isPending}
								onClick={handleSubmit(handleSubmitEmail)}
								disabled={!isDirty}
							>
								{t('send_email')}
							</Button>
							<Button
								variant="ghost"
								size="lg"
								className="w-full"
								onClick={handleCancel}
							>
								{t('back_to_login')}
							</Button>

							<Label.Big300>
								{t('need_help')}
								<Label.Thin300>{t('email_us_at')}</Label.Thin300> &nbsp;
								<a href="mailto:support@getzeal.io">
									<Label.Mid300
										variant="interactive"
										className="hover:text-text-interactive"
									>
										support@getzeal.io
									</Label.Mid300>
								</a>
							</Label.Big300>
						</Flex>
					</Flex>
				</Flex>
			</FormProvider>
		</ParentShimmer>
	);
};

export default ForgotPasswordForm;
